import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { CompanyProvider } from "@/contexts/CompanyContext";
import { NetworkProvider } from "@/contexts/NetworkContext";
import { PaywallProvider } from "@/contexts/PaywallContext";
import { supabase } from "@/integrations/supabase/client";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { GoogleAuth } from "@codetrix-studio/capacitor-google-auth";
import { Purchases, LOG_LEVEL } from "@revenuecat/purchases-capacitor";
import OneSignal from 'onesignal-cordova-plugin';
import { SentryReact } from "@/lib/sentry";
import { ErrorFallback } from "@/components/ErrorFallback";
import { ConsentBanner } from "@/components/ConsentBanner";
import { WelcomeTour } from "@/components/onboarding/WelcomeTour";
import { BackButtonHandler } from "@/components/BackButtonHandler";
import { OfflineDataCacheWarmup } from "@/components/OfflineDataCacheWarmup";
import { GuestQuoteImportPrompt } from "@/components/quotes/GuestQuoteImportPrompt";
import OnboardingPage from "./pages/OnboardingPage";
import LandingPage, { isLandingPlatform } from "./pages/LandingPage";
import DashboardPage from "./pages/DashboardPage";
import ClientsPage from "./pages/ClientsPage";
import ClientFormPage from "./pages/ClientFormPage";
import ClientSummaryPage from "./pages/ClientSummaryPage";
import CatalogPage from "./pages/CatalogPage";
import CatalogItemFormPage from "./pages/CatalogItemFormPage";
import SettingsPage from "./pages/SettingsPage";
import CompanyFormPage from "./pages/CompanyFormPage";
import AuthPage from "./pages/AuthPage";
import AuthCallbackPage from "./pages/AuthCallbackPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import NewQuotePage from "./pages/NewQuotePage";
import QuoteDetailPage from "./pages/QuoteDetailPage";
import QuotesListPage from "./pages/QuotesListPage";
import ReportsPage from "./pages/ReportsPage";
import FinancePage from "./pages/FinancePage";
import ServiceOrdersPage from "./pages/ServiceOrdersPage";
import WorksPage from "./pages/WorksPage";
import ServiceOrderDetailPage from "./pages/ServiceOrderDetailPage";
import AgendaPage from "./pages/AgendaPage";
import AppointmentFormPage from "./pages/AppointmentFormPage";
import ContractsPage from "./pages/ContractsPage";
import ContractDetailPage from "./pages/ContractDetailPage";
import ContractFormPage from "./pages/ContractFormPage";
import FinanceEntryFormPage from "./pages/FinanceEntryFormPage";
import ServiceOrderFormPage from "./pages/ServiceOrderFormPage";
import ContractSignPage from "./pages/ContractSignPage";
import ReceiptsPage from "./pages/ReceiptsPage";
import ReceiptFormPage from "./pages/ReceiptFormPage";
import ReceiptDetailPage from "./pages/ReceiptDetailPage";
import StockPage from "./pages/StockPage";
import QuickSalePage from "./pages/QuickSalePage";
import MessageTemplatesPage from "./pages/MessageTemplatesPage";
import NfsePage from "./pages/NfsePage";
import NfseFormPage from "./pages/NfseFormPage";
import PublicQuotePage from "./pages/PublicQuotePage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import NotificationDetailPage from "./pages/NotificationDetailPage";
import HelpPage from "./pages/HelpPage";
import DeleteAccountPage from "./pages/DeleteAccountPage";
import DeleteDataPage from "./pages/DeleteDataPage";
import ChurnFeedbackPage from "./pages/ChurnFeedbackPage";
import TeamPage from "./pages/TeamPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import AsaasOnboardingPage from "./pages/AsaasOnboardingPage";
import NotFound from "./pages/NotFound";
import { getReferralCodeFromUrl, storeReferralCode } from "@/lib/referral";

// Lazy: a pagina fiscal carrega ~950KB de JSON (LC 214 + atividades municipais);
// mantida fora do bundle inicial e buscada so ao abrir /fiscal.
const FiscalDataPage = lazy(() => import("./pages/FiscalDataPage"));

// Dev-only import for design playground (tree-shaken in production builds)
import TestDashboardPage from "./pages/TestDashboardPage";
import TestNewQuotePage2 from "./pages/TestNewQuotePage2";
import TestNewQuotePage3 from "./pages/TestNewQuotePage3";
import TestNewQuotePage4 from "./pages/TestNewQuotePage4";
import TestOnboardingPage from "./pages/TestOnboardingPage";

// Initialize Google Auth on app start
const initializeGoogleAuth = () => {
  const isNative = Capacitor.isNativePlatform();
  if (isNative) {
    GoogleAuth.initialize({
      clientId: '452590436331-nreihst8rk6us7ehd83fmka1vaj678an.apps.googleusercontent.com',
      scopes: ['profile', 'email'],
      grantOfflineAccess: true,
    });
  }
};

// Initialize RevenueCat for in-app purchases
const initializeRevenueCat = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
      
      // Use platform-specific API keys
      const apiKey = Capacitor.getPlatform() === 'ios' 
        ? '' // iOS key not configured yet
        : 'goog_skixLSMohVVJYSeRffsiZMHUgsj';

      await Purchases.configure({ apiKey });
      console.log('RevenueCat initialized successfully');
    } catch (error) {
      console.error('Error initializing RevenueCat:', error);
    }
  }
};

// Store pending navigation for OneSignal clicks (needs to be outside React)
let pendingNotificationNavigation: string | null = null;

// Initialize OneSignal for push notifications
const initializeOneSignal = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      // Initialize with App ID using the correct API for onesignal-cordova-plugin v5
      OneSignal.initialize("9ba16045-230a-4faf-9d9f-7259181df0d9");
      
      // Request permission for push notifications (required for Android 13+)
      OneSignal.Notifications.requestPermission(true).then((accepted: boolean) => {
        console.log("Permissão de notificação aceita:", accepted);
      });
      
      // Handle notification clicked - store path for navigation
      OneSignal.Notifications.addEventListener('click', (event: any) => {
        console.log('OneSignal notification clicked:', JSON.stringify(event));
        
        // Try multiple paths to get additionalData (different OneSignal SDK versions)
        const additionalData = event.notification?.additionalData || 
                               event.notification?.data ||
                               event.result?.additionalData ||
                               event.data;
        console.log('OneSignal additionalData:', JSON.stringify(additionalData));
        
        if (additionalData) {
          const { notification_id, quote_id, type, link } = additionalData;
          console.log('OneSignal parsed data - notification_id:', notification_id, 'quote_id:', quote_id, 'type:', type);
          
          let targetPath = '/dashboard'; // fallback
          
          // Navigate based on notification data - prefer notification_id for intermediate page
          if (type === 'referral' && link) {
            targetPath = link;
          } else if (notification_id) {
            targetPath = `/notifications/${notification_id}`;
          } else if (quote_id && (type === 'approval' || type === 'rejection' || type === 'view')) {
            // Fallback: if only quote_id, go directly to quote
            targetPath = `/quotes/${quote_id}`;
          }
          
          console.log('OneSignal navigating to:', targetPath);
          
          // Store the path for when React mounts
          pendingNotificationNavigation = targetPath;
          
          // Dispatch custom event to trigger navigation inside React (if already mounted)
          window.dispatchEvent(new CustomEvent('onesignal-navigate', { 
            detail: { path: targetPath }
          }));
        }
      });
      
      // Set external user ID if user is logged in (for targeting notifications)
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        OneSignal.login(user.id);
        console.log('OneSignal: External User ID set to', user.id);
      }
      
      console.log('OneSignal initialized successfully');
    } catch (error) {
      console.error('Error initializing OneSignal:', error);
    }
  }
};

// Call initializations
initializeGoogleAuth();
initializeRevenueCat();
initializeOneSignal();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

// Component to handle OneSignal navigation inside React Router
function OneSignalNavigationHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    // Check if there's a pending navigation from before React mounted
    if (pendingNotificationNavigation) {
      const path = pendingNotificationNavigation;
      pendingNotificationNavigation = null;
      navigate(path);
    }

    // Listen for future navigation events
    const handleNavigate = (event: CustomEvent<{ path: string }>) => {
      console.log('OneSignal navigation event:', event.detail.path);
      navigate(event.detail.path);
    };

    window.addEventListener('onesignal-navigate', handleNavigate as EventListener);

    return () => {
      window.removeEventListener('onesignal-navigate', handleNavigate as EventListener);
    };
  }, [navigate]);

  return null;
}

// Component to handle deep links inside the Router context
function DeepLinkHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    // Only relevant for native deep links (Android/iOS)
    if (!Capacitor.isNativePlatform()) return;

    let listenerHandle: { remove: () => Promise<void> } | null = null;

    const setupDeepLinkListener = async () => {
      listenerHandle = await CapacitorApp.addListener("appUrlOpen", async ({ url }) => {
        console.log("Deep link received:", url);

        // Capture referral attribution before handling auth tokens so redirects
        // and email/Google callbacks cannot discard it.
        const referralCode = getReferralCodeFromUrl(url);
        if (referralCode) {
          storeReferralCode(referralCode);
        }

        // App shortcut de churn (Android): long-press no ícone → "Desinstalando? Nos conte por quê"
        // TODO(iOS): iOS não usa shortcuts.xml; implementar via Home Screen Quick Actions
        // (UIApplicationShortcutItem no AppDelegate + capacitor-plugin próprio).
        if (url.startsWith("orcamigo://churn-feedback")) {
          navigate("/churn-feedback");
          return;
        }

        // Check if it's an email confirmation/login callback link
        const isLoginCallback = url.includes("login-callback") ||
                                url.includes("confirmation") ||
                                url.includes("type=signup") ||
                                url.includes("type=email");

        // Extract tokens from hash or query string
        const hashIndex = url.indexOf("#");
        const queryIndex = url.indexOf("?");

        let params: URLSearchParams | null = null;

        if (hashIndex !== -1) {
          params = new URLSearchParams(url.substring(hashIndex + 1));
        } else if (queryIndex !== -1) {
          params = new URLSearchParams(url.substring(queryIndex + 1));
        }

        if (params) {
          const access_token = params.get("access_token");
          const refresh_token = params.get("refresh_token");

          if (access_token && refresh_token) {
            const { error } = await supabase.auth.setSession({
              access_token,
              refresh_token,
            });

            if (error) {
              console.error("Erro ao definir sessão:", error.message);
              alert("Erro ao confirmar: " + error.message);
            } else {
              // Show success message for email confirmation
              if (isLoginCallback) {
                alert("E-mail confirmado com sucesso! Bem-vindo ao Orçamigo!");
              }
              // Redirect to dashboard
              navigate("/dashboard", { replace: true });
              return;
            }
          }
        }

        if (referralCode && !isLoginCallback) {
          navigate(`/auth?ref=${encodeURIComponent(referralCode)}`, { replace: true });
        }
      });
    };

    setupDeepLinkListener();

    return () => {
      if (listenerHandle) {
        listenerHandle.remove();
      }
    };
  }, [navigate]);

  return null;
}

// Component to poll for email confirmation in native app
function EmailConfirmationPoller() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // Check if we're waiting for email confirmation
    const checkConfirmation = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Session exists, user is logged in
        return;
      }

      // Poll for session changes from external confirmation
      const interval = setInterval(async () => {
        const { data: { session: newSession } } = await supabase.auth.getSession();
        if (newSession) {
          clearInterval(interval);
          // Session was established externally (e.g., from deep link)
          window.location.reload();
        }
      }, 2000); // Check every 2 seconds

      // Stop polling after 5 minutes
      setTimeout(() => clearInterval(interval), 300000);

      return () => clearInterval(interval);
    };

    checkConfirmation();
  }, []);

  return null;
}

const App = () => (
  <SentryReact.ErrorBoundary fallback={<ErrorFallback />}>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <PaywallProvider>
              <CompanyProvider>
                <NetworkProvider>
              <OfflineDataCacheWarmup />
              <GuestQuoteImportPrompt />
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <ConsentBanner />
                <BackButtonHandler />
                <DeepLinkHandler />
                <OneSignalNavigationHandler />
                <EmailConfirmationPoller />
                <WelcomeTour />
                <Routes>
                  {/* A home tem duas caras: VITRINE na web (quem ainda não
                      conhece o produto chega por aqui) e o ONBOARDING dentro do
                      app instalado, onde uma propaganda seria anúncio para quem
                      já baixou. A régua é a PLATAFORMA e não o domínio, porque
                      `app.orcamigo.com.br` responde 302 para o site — o
                      hostname não distingue os dois casos. */}
                  <Route path="/" element={isLandingPlatform() ? <LandingPage /> : <OnboardingPage />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  {import.meta.env.DEV && (
                    <>
                      <Route path="/test-dashboard" element={<TestDashboardPage />} />
                      <Route path="/test-onboarding" element={<TestOnboardingPage />} />
                      <Route path="/test-novo-orcamento-2" element={<TestNewQuotePage2 />} />
                      <Route path="/test-novo-orcamento-3" element={<TestNewQuotePage3 />} />
                      <Route path="/test-novo-orcamento-4" element={<TestNewQuotePage4 />} />
                    </>
                  )}
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/auth/callback" element={<AuthCallbackPage />} />
                  <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/clients" element={<ClientsPage />} />
                  <Route path="/clients/new" element={<ClientFormPage />} />
                  <Route path="/clients/:id/edit" element={<ClientFormPage />} />
                  <Route path="/clients/:id" element={<ClientSummaryPage />} />
                  <Route path="/catalog" element={<CatalogPage />} />
                  <Route path="/catalog/new" element={<CatalogItemFormPage />} />
                  <Route path="/catalog/:id/edit" element={<CatalogItemFormPage />} />
                  <Route path="/quotes" element={<QuotesListPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/companies/new" element={<CompanyFormPage />} />
                  <Route path="/companies/:id/edit" element={<CompanyFormPage />} />
                  {/*
                    Caminho de TESTE do BaaS (sandbox), não parte do lançamento
                    congelado — ver o cabeçalho de `AsaasOnboardingPage.tsx`.
                    A própria tela recusa quem não é admin/owner e a Edge
                    Function recusa quem não tem o plano de teste.
                  */}
                  <Route path="/pagamentos/conectar-teste" element={<AsaasOnboardingPage />} />
                  <Route path="/equipe" element={<TeamPage />} />
                  <Route path="/convite/:token" element={<AcceptInvitePage />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/financeiro" element={<FinancePage />} />
                  <Route path="/financeiro/novo" element={<FinanceEntryFormPage />} />
                  <Route path="/financeiro/:id/editar" element={<FinanceEntryFormPage />} />
                  <Route path="/trabalhos" element={<WorksPage />} />
                  <Route path="/ordens-servico" element={<ServiceOrdersPage />} />
                  <Route path="/ordens-servico/nova" element={<ServiceOrderFormPage />} />
                  <Route path="/ordens-servico/:id/editar" element={<ServiceOrderFormPage />} />
                  <Route path="/ordens-servico/:id" element={<ServiceOrderDetailPage />} />
                  <Route path="/agenda" element={<AgendaPage />} />
                  <Route path="/agenda/novo" element={<AppointmentFormPage />} />
                  <Route path="/agenda/:id/editar" element={<AppointmentFormPage />} />
                  <Route path="/contratos" element={<ContractsPage />} />
                  <Route path="/contratos/novo" element={<ContractFormPage />} />
                  <Route path="/contratos/:id/editar" element={<ContractFormPage />} />
                  <Route path="/contratos/:id" element={<ContractDetailPage />} />
                  <Route path="/recibos" element={<ReceiptsPage />} />
                  <Route path="/recibos/novo" element={<ReceiptFormPage />} />
                  <Route path="/recibos/:id/editar" element={<ReceiptFormPage />} />
                  <Route path="/recibos/:id" element={<ReceiptDetailPage />} />
                  <Route path="/estoque" element={<StockPage />} />
                  <Route path="/venda" element={<QuickSalePage />} />
                  <Route path="/modelos-mensagens" element={<MessageTemplatesPage />} />
                  <Route path="/nfse" element={<NfsePage />} />
                  <Route path="/nfse/nova" element={<NfseFormPage />} />
                  <Route path="/quotes/new" element={<NewQuotePage />} />
                  <Route path="/quotes/:id" element={<QuoteDetailPage />} />
                  <Route path="/o/:id" element={<PublicQuotePage />} />
                  <Route path="/c/:token" element={<ContractSignPage />} />
                  <Route path="/notifications/:id" element={<NotificationDetailPage />} />
                  <Route path="/help" element={<HelpPage />} />
                  <Route path="/delete-account" element={<DeleteAccountPage />} />
                  <Route path="/delete-data" element={<DeleteDataPage />} />
                  <Route path="/churn-feedback" element={<ChurnFeedbackPage />} />
                  <Route
                    path="/fiscal"
                    element={
                      <Suspense fallback={<div className="p-8 text-center text-sm text-muted-foreground">Carregando…</div>}>
                        <FiscalDataPage />
                      </Suspense>
                    }
                  />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </BrowserRouter>
                </NetworkProvider>
              </CompanyProvider>
            </PaywallProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  </SentryReact.ErrorBoundary>
);

export default App;
